import { TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { GridRowParams } from '@mui/x-data-grid';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRedirectOnAuth } from '../../aws/AuthHooks';
import { AuthState } from '../../aws/AuthProvider';
import { CustomDataGrid } from '../../components/data-grid/CustomDataGrid';
import { useStyles } from '../../theme/styles.helpers';
import { ROUTE } from '../routes';
import { PresignRequestsStyles } from './PresignRequests.Styles';
import { DataContext } from '../../aws/DataProvider';
import { CustomModal } from '../../components/modal/CustomModal';
import PresignCreateModal from '../../components/modals/presign-create/index';
import { listPresignRequests } from '../../aws/RequestsApi';
import { AuthContext } from '../../aws/AuthProvider';
import { RefreshButton } from '../../components/button-components/refresh-button/RefreshButton';
import { QrCodeButton } from '../../components/button-components/qrcode-button/QrCodeButton';


interface PresignRequestRow {
  id: string; // Added for DataGrid row id
  requestId: string;
  email: string;
  createdAt: Date;
  state: string;
}

const PresignRequests: FC = () => {
  useRedirectOnAuth([AuthState.UNAUTHORIZED], ROUTE.LOGIN);
  const {
    presignRequestsState: [presignRequests, setPresignRequests],
  } = useContext(DataContext);
  const { apiClient } = useContext(AuthContext);
  const { t } = useTranslation();
  const styles = useStyles(PresignRequestsStyles, {});
  const [rows, setRows] = useState<PresignRequestRow[]>([]);
  const [freeTextSearch, setFreeTextSearch] = useState<string>('');
  const [selectedRequest, setSelectedRequest] = useState<{requestId: string; email:string;} | null>(null);
  const [showQrCode, setShowQrCode] = useState<boolean>(false);

  useEffect(() => {
    if (presignRequests) {
      const _rows = presignRequests.map((request) => ({
        id: request.requestId, // Use index as id for DataGrid
        requestId: request.requestId, // Use index as id for DataGrid
        email: request.email,
        createdAt: new Date(request.createdAt),
        state: request.orderState,
      }));
      setRows(_rows);
    }
  }, [presignRequests]);

  useEffect(() => {
    if (!freeTextSearch) {
      if (presignRequests) {
        const _rows = presignRequests.map((request) => ({
          id: request.requestId,
          requestId: request.requestId,
          email: request.email,
          createdAt: new Date(request.createdAt),
          state: request.orderState,
        }));
        setRows(_rows);
      }
      return;
    }
    const _rows = rows.filter((row) => {
      return Object.values(row).some((value) => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(freeTextSearch.toLowerCase());
        }
        return false;
      });
    });
    setRows(_rows);
  }, [freeTextSearch, presignRequests]);

  const handleRowClicked = (params: GridRowParams) => {
    // For now, just log the clicked row
    if (params.row.state === 'IDLE') setSelectedRequest(params.row);
  };

  const refetchPresignRequests = async () => {
    if (apiClient) {
      try {
        const data = await listPresignRequests(apiClient, 'network-only');
        setPresignRequests(data);
      } catch (error) {
        console.error('Error refetching presign requests:', error);
      }
    }
  };

  const columns = [
    {
      field: 'email',
      headerName: t('E-Mail'),
      width: 400,
    },
    {
      field: 'createdAt',
      headerName: t('Created'),
      width: 300,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      renderCell: (params: any) => {
        return new Date(params.value).toLocaleString();
      },
    },
    {
      field: 'state',
      headerName: t('Status'),
      width: 200,
    },
  ];

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h3">{t('Presign Requests')}</Typography>
        <Box display={'flex'} gap={2}>
          <TextField
            label={t('Search')}
            variant="outlined"
            value={freeTextSearch}
            onChange={(e) => setFreeTextSearch(e.target.value)}
            sx={{ width: 300 }}
            InputLabelProps={{ sx: { textIndent: '10px', fontSize: '18px' } }}
          />
          <RefreshButton onClick={refetchPresignRequests} />
          <QrCodeButton onClick={() => setShowQrCode(true)} />

        </Box>
      </Box>

      <CustomDataGrid
        rows={rows}
        columns={columns}
        onRowClick={handleRowClicked}
        getRowClassName={(params) => (params.row.state === 'ALLOWED' ? 'allowed-row' : '')}
        sx={{
          ...styles.row,
          '& .MuiDataGrid-row.allowed-row': {
            opacity: 0.4,
          },
        }}
        autoPageSize
      />
      {selectedRequest && (
        <CustomModal isModalOpen={!!selectedRequest} onModalClose={() => setSelectedRequest(null)} variant="large">
          <PresignCreateModal 
            onModalClose={() => setSelectedRequest(null)}
            selectedRequest={selectedRequest}
            onRefetch={refetchPresignRequests}
          />
        </CustomModal>
      )}
      {showQrCode && (
        <CustomModal isModalOpen={showQrCode} onModalClose={() => setShowQrCode(false)} variant="small">
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <img src="/images/default-doctor-presign.png" alt="qr" />
          </Box>
        </CustomModal>
      )}
    </Box>
  );
};

export default PresignRequests;
